import {
  Banner,
  BoundSelectField,
  Button,
  Css,
  GridColumn,
  GridDataRow,
  GridTable,
  IconButton,
  column,
  numericColumn,
  simpleHeader,
  useComputed,
  useModal,
} from "@homebound/beam";
import { ObjectConfig, ObjectState, useFormState } from "@homebound/form-state";
import { Observer } from "mobx-react";
import { priceCell } from "src/components";
import { DisplayNamedFragment } from "src/fragments/commonFragments.generated";
import { BidContractType, SaveBidPackageAwardInput, SaveBidPackageAwardsInput } from "src/generated/graphqlTypes";
import { BidPackageDetailPageDocument } from "src/routes/bids/BidPackageDetailPage.generated";
import {
  AwardBidModal_BidPackageFragment,
  useAwardBidModalQuery,
  useSaveBidPackageAwardsMutation,
} from "src/routes/bids/components/AwardBidModal.generated";
import { isDefined, queryResult } from "src/utils";
import { getBidPackageTradesWithEstimate } from "./utils";

type AwardBidModalProps = {
  bidPackageId: string;
};

export function AwardBidModal({ bidPackageId }: AwardBidModalProps) {
  const query = useAwardBidModalQuery({ variables: { bidPackageId }, fetchPolicy: "network-only" });
  return queryResult(query, ({ bidPackage: awardBidPackage }) => <AwardBidModalView bidPackage={awardBidPackage} />);
}

type AwardBidModalViewProps = {
  bidPackage: AwardBidModal_BidPackageFragment;
};
export function AwardBidModalView({ bidPackage }: AwardBidModalViewProps) {
  const { closeModal } = useModal();
  const [saveBidPackageAwards] = useSaveBidPackageAwardsMutation({ refetchQueries: [BidPackageDetailPageDocument] });

  const formState = useFormState({
    config: formConfig,
    init: {
      input: bidPackage,
      map: (bidPackage) => ({ bidPackageAwards: mapOrCreateBidPackageAwards(bidPackage) }),
    },
  });

  const newOverlappingCostCodes = useComputed(() => {
    const newAwards = formState.bidPackageAwards.value.filter(
      (award) => award.primaryRevisionId !== undefined && !isDefined(award.id),
    );
    // If we are creating new awards we should check if there are any overlapping bid packages / cost codes
    if (newAwards.nonEmpty) {
      const newCostCodes = newAwards.map((bp) => bp.costCodeId!);
      const allOverlappingCostCodes = bidPackage.overlappingBidPackages
        .flatMap((bp) => bp.latestAwards)
        .map((a) => a.costCode)
        .uniqueByKey("id");

      return allOverlappingCostCodes.filter((costCode) => newCostCodes.includes(costCode.id));
    }
    return [];
  }, [bidPackage, formState]);

  async function saveAndExit() {
    await saveBidPackageAwards({
      variables: {
        input: {
          // todo add test for this
          bidPackageAwards: formState.bidPackageAwards.value.filter((award) => award.primaryRevisionId !== undefined),
        },
      },
    });
    closeModal();
  }

  return (
    <div css={Css.df.fdc.p2.bgGray100.h100.$}>
      <div css={Css.asfe.pr1.$}>
        <IconButton icon="x" onClick={closeModal} />
      </div>
      {newOverlappingCostCodes.nonEmpty && (
        <div css={Css.relative.z1.$}>
          <Banner
            message={`A bid package already exists for ${newOverlappingCostCodes.map((cc) => cc.displayName).join(", ")}. Continuing will overwrite the existing bid package.`}
            type="warning"
          />
        </div>
      )}
      <div css={Css.xl2.asc.mt1.$} data-testid="award_title">
        Award Bid
      </div>
      <div css={Css.md.asc.pt2.$}>
        Here you can assign primary and secondary trades for specific scopes of work. After saving, you'll be able to
      </div>
      <div css={Css.md.asc.$}>award and release work to the selected trades when needed.</div>
      <div css={Css.p4.$}>
        <GridTable columns={createColumns(bidPackage, formState)} rows={createRows(bidPackage)} />
      </div>
      <div css={Css.fixed.bottom0.w100.px6.py3.bshHover.df.$}>
        <div css={Css.mla.$}>
          <Observer>
            {() => <Button label="Save & Return" onClick={saveAndExit} size="lg" disabled={!formState.dirty} />}
          </Observer>
        </div>
      </div>
    </div>
  );
}

function createColumns(
  bidPackage: AwardBidModal_BidPackageFragment,
  formState: ObjectState<SaveBidPackageAwardsInput>,
): GridColumn<Row>[] {
  const trades = getBidPackageTradesWithEstimate(bidPackage);
  return [
    column<Row>({
      header: "Cost Code",
      costCode: (cc) => cc.displayName,
    }),
    // Generate a column for each BPR which is unqiue to trade partner
    ...trades.map(({ tradePartner, bidContractRevision }) => {
      return numericColumn<Row>({
        header: tradePartner.name,
        // For each cost code, sum the total cost for all line items that have the same cost code
        costCode: (cc) => {
          // if bid contract type is unit based then use the cost from the bcli
          if (bidContractRevision?.bidContract?.type?.code === BidContractType.UnitBased) {
            // Scope line tlis contain the quantity of items
            const scopeLineTlis = bidPackage.scopeLines.map((sl) => sl.takeoffLineItemVersion);
            // For plan bids, from all the line items on the bid contract
            const planBasedValue =
              bidContractRevision?.lineItems
                // only get the line items that match the cost code
                .filter((bcli) => bcli.bidItem?.items.first?.costCode.id === cc.id)
                // for each BCLI, I need to get all the associated scope line tlis for this cost code and sum the quantity
                .map(
                  (bcli) =>
                    (bcli.totalCostInCents ?? 0) *
                    scopeLineTlis
                      .filter((slTli) => {
                        return slTli.bidItem?.id === bcli.bidItem?.id && slTli.item.costCode.id === cc.id;
                      })
                      .map((slTli) => slTli.quantity ?? 0)
                      .sum(),
                )
                .sum() ?? 0;
            const valueInCents = bidPackage.isUnitBased
              ? // For unit bids, sum the direct bcli total for the matching costCode bid item
                bidContractRevision?.lineItems
                  .filter((bcli) => bcli.bidItem?.items.first?.costCode.id === cc.id)
                  .sum((bcli) => bcli.totalCostInCents ?? 0)
              : planBasedValue;

            return priceCell({ valueInCents });
          }
          // if the bids are prorated use the bcli with a iti that matches the cost code
          const proratedValueInCents =
            bidContractRevision?.lineItems // From all the line items on the bid contract
              .filter((bcli) => bcli.itemTemplateItem?.item.costCode.id === cc.id) // only get the line items that match the cost code
              .map((bcli) => bcli.totalCostInCents ?? 0)
              .sum() ?? 0;
          // if the bids are prorated use the price from the iti on the bcli
          return priceCell({ valueInCents: proratedValueInCents });
        },
      });
    }),
    column<Row>({
      header: "Primary",
      costCode: (cc) => (
        <BoundSelectField
          data-testid="primary"
          options={trades}
          field={formState.bidPackageAwards.rows.find((r) => r.costCodeId.value === cc.id)!.primaryRevisionId}
          key={cc.id}
          getOptionLabel={(r) => r.tradePartner.name}
          getOptionValue={(r) => r.bidContractRevision?.id ?? ""}
        />
      ),
    }),
    column<Row>({
      header: "Secondary",
      costCode: (cc) => (
        <BoundSelectField
          data-testid="secondary"
          options={trades}
          field={formState.bidPackageAwards.rows.find((r) => r.costCodeId.value === cc.id)!.secondaryRevisionId}
          key={cc.id}
          getOptionLabel={(r) => r.tradePartner.name}
          getOptionValue={(r) => r.bidContractRevision?.id ?? ""}
        />
      ),
    }),
  ];
}

function createRows(bidPackage: AwardBidModal_BidPackageFragment): GridDataRow<Row>[] {
  const unitBidLineItems = bidPackage.lineItems
    .map((bpli) => bpli.bidItem.items.first?.costCode)
    .compact()
    .uniqueByKey("id")
    .map((cc) => ({
      kind: "costCode" as const,
      id: cc.id,
      data: cc,
    }));

  const planBidLineItems = bidPackage.scopeLines
    .map((sl) => sl.takeoffLineItemVersion.item.costCode)
    .uniqueByKey("id")
    .map((cc) => ({
      kind: "costCode" as const,
      id: cc.id,
      data: cc,
    }));
  return [simpleHeader, ...(bidPackage.isUnitBased ? unitBidLineItems : planBidLineItems)];
}

type HeaderRow = { kind: "header"; data: undefined };
type CostCodeRow = {
  kind: "costCode";
  data: DisplayNamedFragment;
};

type Row = HeaderRow | CostCodeRow;

const formConfig: ObjectConfig<SaveBidPackageAwardsInput> = {
  bidPackageAwards: {
    type: "list",
    config: {
      id: { type: "value" },
      costCodeId: { type: "value" },
      primaryRevisionId: { type: "value" },
      secondaryRevisionId: { type: "value" },
      bidPackageVersionId: { type: "value" },
    },
  },
};

function mapOrCreateBidPackageAwards(bidPackage: AwardBidModal_BidPackageFragment) {
  // get all the cost codes
  const costCodes = bidPackage.isUnitBased
    ? bidPackage.lineItems
        .map((bpli) => bpli.bidItem.items.first?.costCode)
        .compact()
        .uniqueByKey("id")
    : bidPackage.scopeLines.map((sl) => sl.takeoffLineItemVersion.item.costCode).uniqueByKey("id");
  // Need to create a row for each cost code that does not exist
  return costCodes.map<SaveBidPackageAwardInput>((cc) => {
    const award = bidPackage.awards.find((award) => award.costCode.id === cc.id);
    return {
      costCodeId: cc.id,
      id: award?.id,
      primaryRevisionId: award?.primaryRevision.id,
      secondaryRevisionId: award?.secondaryRevision?.id,
      bidPackageVersionId: bidPackage.latestVersion.id,
    };
  });
}
